// Toast on new responses to estimates we sent out via estshare__. Shares the
// React Query cache with AlertBell (useOutgoingSharedEstimates). Per-uid
// seen-set in localStorage prevents re-toasting across polls or reloads.
//
// First-ever load (no localStorage entry) seeds silently so historical
// accept/decline responses don't flood the user on app launch.
//
// Side effect: when a new response is seen, flip the sender's local Estimate
// row to the matching status so the Estimates list reflects the response
// beyond just the bell.

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { qk } from './queryKeys'
import { toastOk, toastErr } from '../lib/toastStore'
import { useOutgoingSharedEstimates } from './useOutgoingSharedEstimates'
import { saveSlice } from '../lib/db'
import type { AppState } from '../types/firestore'
import type { Estimate } from '../types/domain'
import type { SharedEstimateDoc } from '../lib/db/shared'

const CAP = 200

function keyFor(uid: string): string {
  return `jsp.outgoingEstSeen.${uid}`
}

function readSeenRaw(uid: string): string | null {
  try { return localStorage.getItem(keyFor(uid)) } catch { return null }
}

function parseSeen(raw: string | null): string[] {
  if (raw === null) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch { return [] }
}

function writeSeen(uid: string, ids: string[]): void {
  try {
    const trimmed = ids.length > CAP ? ids.slice(ids.length - CAP) : ids
    localStorage.setItem(keyFor(uid), JSON.stringify(trimmed))
  } catch { /* quota — ignore */ }
}

function pickClientName(doc: SharedEstimateDoc): string | undefined {
  const d = doc as unknown as { toName?: unknown; clientName?: unknown }
  if (typeof d.toName === 'string' && d.toName) return d.toName
  if (typeof d.clientName === 'string' && d.clientName) return d.clientName
  return undefined
}

export function useOutgoingEstimateAlerts(): void {
  const { effectiveUid } = useAuth()
  const { data } = useOutgoingSharedEstimates()
  const qc = useQueryClient()

  useEffect(() => {
    if (!effectiveUid || !data) return
    const uid = effectiveUid
    const raw = readSeenRaw(uid)

    // First-ever load: seed silently with current IDs.
    if (raw === null) {
      writeSeen(uid, data.map((d) => d.id))
      return
    }

    const prior = parseSeen(raw)
    const seen = new Set(prior)
    const fresh = data.filter((d) => !seen.has(d.id))
    if (fresh.length === 0) return

    for (const d of fresh) {
      const name = d.estimateSnapshot?.name || 'Estimate'
      const who = pickClientName(d)
      if (d.status === 'accepted') {
        toastOk(who ? `✅ ${who} approved your estimate` : `✅ ${name} was approved`)
      } else if (d.status === 'declined') {
        toastErr(who ? `✖ ${who} declined your estimate` : `✖ ${name} was declined`)
      }
      seen.add(d.id)

      // Best-effort local-flip of the sender's Estimate row.
      try {
        const state = qc.getQueryData<AppState>(qk.appstate(uid))
        if (!state) continue
        const snapId = d.estimateSnapshot?.id
        const snapName = d.estimateSnapshot?.name
        let idx = -1
        if (typeof snapId === 'number') {
          idx = state.estimates.findIndex((e) => e.id === snapId)
        }
        if (idx < 0 && typeof snapName === 'string' && snapName) {
          const matches = state.estimates
            .map((e, i) => ({ e, i }))
            .filter(({ e }) => e.name === snapName)
          if (matches.length > 0) {
            matches.sort((a, b) => {
              const da = a.e.date ? new Date(a.e.date).getTime() : 0
              const db = b.e.date ? new Date(b.e.date).getTime() : 0
              return db - da
            })
            idx = matches[0]!.i
          }
        }
        if (idx < 0) continue
        const cur = state.estimates[idx]!
        if (cur.status === 'accepted' || cur.status === 'declined' || cur.status === 'expired') continue
        const nextStatus: Estimate['status'] = d.status === 'accepted' ? 'accepted' : 'declined'
        const nextEst: Estimate = { ...cur, status: nextStatus }
        const nextEstimates = state.estimates.slice()
        nextEstimates[idx] = nextEst
        const nextState: AppState = { ...state, estimates: nextEstimates }
        qc.setQueryData(qk.appstate(uid), nextState)
        void saveSlice(uid, 'estimates', nextEstimates).catch((err) => {
          console.warn('outgoing-est flip save failed', err)
        })
      } catch (err) {
        console.warn('outgoing-est flip failed', err)
      }
    }

    writeSeen(uid, [...prior, ...fresh.map((d) => d.id)])
  }, [effectiveUid, data, qc])
}
